import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { matchDATEquipmentSearch } from 'clerk_common/constants/urls/datEquipmentSearch';
import { useCallback, useMemo, useRef } from 'react';
import {
  getDatEquipmentSearchChat,
  rememberDatEquipmentSearchChat,
} from './datEquipmentSearchChats';
import { PageChat } from './pageChat';
import { useChangeCount } from './useChangeCount';

export function useDatEquipmentSearchChat(pageUrl: string) {
  const { defaultOrgId } = useMeContext();
  const searchUrl = matchDATEquipmentSearch(pageUrl) ? pageUrl : null;
  const visitId = useChangeCount(`${defaultOrgId}|${searchUrl}`);
  const initialVisitId = useInitialSearchVisitId({
    isReady: Boolean(defaultOrgId),
    isOnSearch: Boolean(searchUrl),
    visitId,
  });

  const pageChat = useMemo<PageChat | undefined>(() => {
    if (!searchUrl || !defaultOrgId) return undefined;
    return {
      visitId,
      conversationId: getDatEquipmentSearchChat(defaultOrgId, searchUrl),
      isResolved: true,
      isFinal: true,
      hasFailed: false,
      isInitialVisit: visitId === initialVisitId,
    };
  }, [defaultOrgId, initialVisitId, searchUrl, visitId]);

  const rememberConversation = useCallback(
    (conversationId: string | null) => {
      if (!searchUrl || !defaultOrgId || !conversationId) return;
      rememberDatEquipmentSearchChat(defaultOrgId, searchUrl, conversationId);
    },
    [defaultOrgId, searchUrl]
  );

  return { pageChat, rememberConversation };
}

// NOTE(mike): Only a search the panel opened on is an initial visit; a search
// reached from another page must replace that page's open chat.
function useInitialSearchVisitId({
  isReady,
  isOnSearch,
  visitId,
}: {
  isReady: boolean;
  isOnSearch: boolean;
  visitId: number;
}) {
  const firstVisit = useRef<{ visitId: number; isOnSearch: boolean } | null>(
    null
  );
  if (isReady && !firstVisit.current) {
    firstVisit.current = { visitId, isOnSearch };
  }
  return firstVisit.current?.isOnSearch ? firstVisit.current.visitId : null;
}
